/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string) => ["search", "results", query] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Local feed (stream activity_recent). ALWAYS with userId: isolates cache between accounts. */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Local history of a specific entity. */
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  gamification: {
    all: ["gamification"] as const,
    /** Game state (XP/level/streak/badges) derived from the activity feed. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
  },
  trash: {
    all: ["trash"] as const,
    products: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "products", userId, orgId] as const,
    warehouses: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "warehouses", userId, orgId] as const,
    locations: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "locations", userId, orgId] as const,
    pickings: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "pickings", userId, orgId] as const,
  },
  inventory: {
    all: ["inventory"] as const,
    /** Products of an organization. */
    products: (orgId: string | undefined) => ["inventory", "products", orgId] as const,
    /** Map id→name of products (trash included, for the feed). */
    productNames: (orgId: string | undefined) => ["inventory", "productNames", orgId] as const,
    /** Warehouses of an organization. */
    warehouses: (orgId: string | undefined) => ["inventory", "warehouses", orgId] as const,
    /** Locations of an organization. */
    locations: (orgId: string | undefined) => ["inventory", "locations", orgId] as const,
    /** Quants (on-hand) of an organization. */
    quants: (orgId: string | undefined) => ["inventory", "quants", orgId] as const,
    /** Product categories. */
    categories: (orgId: string | undefined) => ["inventory", "categories", orgId] as const,
    /** Units of measure. */
    uoms: (orgId: string | undefined) => ["inventory", "uoms", orgId] as const,
    /** Operations (pickings) of an organization. */
    pickings: (orgId: string | undefined) => ["inventory", "pickings", orgId] as const,
    /** Lines of an operation. */
    pickingLines: (pickingId: string | undefined) =>
      ["inventory", "pickingLines", pickingId] as const,
    /** Reorder rules of an organization. */
    reorderRules: (orgId: string | undefined) => ["inventory", "reorderRules", orgId] as const,
    /** Vendors of an organization (Stockpilot F3). */
    vendors: (orgId: string | undefined) => ["inventory", "vendors", orgId] as const,
    /** Vendor-product prices from an organization. */
    suppliers: (orgId: string | undefined) => ["inventory", "suppliers", orgId] as const,
    /** Purchase orders of an organization. */
    purchaseOrders: (orgId: string | undefined) => ["inventory", "purchaseOrders", orgId] as const,
    /** Lines of a purchase order. */
    purchaseOrderLines: (poId: string | undefined) =>
      ["inventory", "purchaseOrderLines", poId] as const,
    /** Valuation layers of an organization. */
    valuationLayers: (orgId: string | undefined) =>
      ["inventory", "valuationLayers", orgId] as const,
    /** Lots / serials of an organization (Stockpilot F4). */
    lots: (orgId: string | undefined) => ["inventory", "lots", orgId] as const,
    /** Lots of a specific product. */
    lotsByProduct: (productId: string | undefined) =>
      ["inventory", "lotsByProduct", productId] as const,
    /** Quants by (product, location) for FEFO. */
    quantsByLocation: (productId: string | undefined, locationId: string | undefined) =>
      ["inventory", "quantsByLocation", productId, locationId] as const,
    /** Count documents of an organization. */
    counts: (orgId: string | undefined) => ["inventory", "counts", orgId] as const,
    /** Lines of a count document. */
    countLines: (countId: string | undefined) => ["inventory", "countLines", countId] as const,
    /** Bills of materials (BoM) of an organization (Stockpilot F5). */
    boms: (orgId: string | undefined) => ["inventory", "boms", orgId] as const,
    /** Components of a BoM. */
    bomComponents: (bomId: string | undefined) => ["inventory", "bomComponents", bomId] as const,
    /** Manufacturing orders of an organization. */
    manufacturingOrders: (orgId: string | undefined) =>
      ["inventory", "manufacturingOrders", orgId] as const,
    /** Landed costs of an organization. */
    landedCosts: (orgId: string | undefined) => ["inventory", "landedCosts", orgId] as const,
    /** Lines (allocation) of a landed cost. */
    landedCostLines: (landedCostId: string | undefined) =>
      ["inventory", "landedCostLines", landedCostId] as const,
    /** Receipts (pickings of type receipt) of an organization. */
    receipts: (orgId: string | undefined) => ["inventory", "receipts", orgId] as const,
    /** Vendor returns of an organization. */
    purchaseReturns: (orgId: string | undefined) =>
      ["inventory", "purchaseReturns", orgId] as const,
    /** Lines of a vendor return. */
    purchaseReturnLines: (returnId: string | undefined) =>
      ["inventory", "purchaseReturnLines", returnId] as const,
    /**
     * User organizations synced in LOCAL (PowerSync) for the
     * multi-organization switcher. Different key from `auth.organizations()`
     * (that is the API list on the organizations page): they share data
     * but not cache, to avoid clashing between local-first and network reads.
     */
    memberships: () => ["inventory", "memberships", "local"] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Attachments scoped to an owner record (e.g. a product). */
    byOwner: (ownerId: string) => ["attachments", ownerId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    /**
     * List of active sessions for the profile. `sessions()` is the prefix that
     * invalidates ALL variants; `sessionsFor(id)` is the concrete key used by
     * the screen (depends on which session is "from this device").
     * Previously was the loose array `["active-sessions", id]` written by hand on
     * the screen — invisible to the `queryKey` guard because `createApiQuery`
     * receives it as an argument, not as a `queryKey:` property.
     */
    sessions: () => ["auth", "activeSessions"] as const,
    sessionsFor: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", currentSessionId ?? null] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
